import React, { useState, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useUserProfile } from '../contexts/UserProfileContext';
import { Camera, Loader2 } from 'lucide-react';
import { RecruiterPageShell } from '../components/Recruiter/RecruiterStates';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const WelcomeRecruiter: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user, apiCall, getToken } = useAuth();
  const { userProfile } = useUserProfile();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [recruiterBackground, setRecruiterBackground] = useState('');
  const [consentGranted, setConsentGranted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  if (userProfile && !userProfile.is_recruiter) {
    return <Navigate to="/" replace />;
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError(t('welcomeRecruiter.errorImageSize', 'Image must be under 5MB'));
      return;
    }
    setProfileImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setProfileImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const uploadProfileImage = async () => {
    if (!profileImage || !user?.userId) return;
    setUploadingImage(true);
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', profileImage);
      await fetch(`${API_BASE}/api/v1/profiles/${user.userId}/profile-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
    } catch {} finally { setUploadingImage(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.debug('🚀 Submit clicked!', { firstName, lastName, companyName, recruiterBackground, consentGranted, userId: user?.userId });
    
    // Validation
    if (!firstName.trim()) {
      setError(t('welcomeRecruiter.errorFirstName'));
      return;
    }
    
    if (!lastName.trim()) {
      setError(t('welcomeRecruiter.errorLastName'));
      return;
    }
    
    if (!companyName.trim()) {
      setError(t('welcomeRecruiter.errorCompanyName'));
      return;
    }
    
    if (!consentGranted) {
      setError(t('welcomeRecruiter.errorConsent'));
      return;
    }

    if (!user?.userId) {
      console.error('❌ User not authenticated', user);
      setError(t('welcomeRecruiter.errorAuth'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.debug('📡 Updating profile for user:', user.userId);

      // Step 1: Update profile
      const profileResponse = await apiCall(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/profiles/${user.userId}`, {
        method: 'PUT',
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          company_name: companyName,
          recruiter_background: recruiterBackground || null
        })
      });

      if (!profileResponse.ok) {
        throw new Error('Failed to update profile');
      }

      // Get the profile UUID from response (might be different from Supabase UUID)
      const profileData = await profileResponse.json();
      const profileUuid = profileData.id;
      console.debug('✅ Profile updated, UUID:', profileUuid);

      // Step 2: Record consent (use by-uuid endpoint which works with Supabase UUID)
      console.debug('📡 Recording recruiter visibility consent...');
      const consentResponse = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/profiles/by-uuid/${user.userId}/consents`,
        {
          method: 'POST',
          body: JSON.stringify({
            consent_type: 'recruiter_profile_visibility',
            granted: true,
            version: '1.0'
          })
        }
      );

      if (!consentResponse.ok) {
        const errorText = await consentResponse.text();
        console.error('Consent error:', errorText);
        throw new Error('Failed to record consent');
      }

      console.debug('✅ Profile and consent updated successfully');

      if (profileImage) {
        await uploadProfileImage();
      }

      navigate('/?tab=hire');
    } catch (err) {
      console.error('Profile update error:', err);
      setError(t('welcomeRecruiter.errorSave'));
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // Skip to hire tab (home for recruiters)
    navigate('/?tab=hire');
  };

  const inputClass =
    'w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-colors bg-white';

  return (
    <RecruiterPageShell contentClassName="max-w-2xl">
      <div className="py-8 sm:py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mb-3">
            {t('welcomeRecruiter.title')}
          </h1>
          <p className="text-base sm:text-lg text-slate-500">
            {t('welcomeRecruiter.subtitle')}
          </p>
        </div>

        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
          <p className="text-sm text-slate-600 leading-relaxed">
            <span className="font-semibold text-pink-600">{t('welcomeRecruiter.reviewHint.label', 'Heads up:')}</span>{' '}
            {t(
              'welcomeRecruiter.reviewHint.text',
              'Dein Hiring Board ist sofort einsatzbereit. Dein öffentliches Unternehmensprofil im Discovery-Feed schalten wir frei, sobald wir es kurz gereviewed haben.'
            )}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 mb-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="flex justify-center">
              <div
                className="relative cursor-pointer group"
                onClick={() => imageInputRef.current?.click()}
              >
                {profileImagePreview ? (
                  <img src={profileImagePreview} alt="" className="w-24 h-24 rounded-full object-cover border-4 border-slate-200 shadow-sm" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center border-4 border-slate-200 shadow-sm">
                    <Camera className="w-8 h-8 text-slate-400" />
                  </div>
                )}
                <div className="absolute inset-0 rounded-full bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera className="w-6 h-6 text-white" />
                </div>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>
            </div>
            <p className="text-center text-xs text-slate-400 -mt-2">
              {t('welcomeRecruiter.profileImageHint', 'Profilbild hochladen (optional)')}
            </p>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                {t('welcomeRecruiter.firstNameLabel')}
                <span className="text-red-500 ml-1">{t('welcomeRecruiter.required')}</span>
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={inputClass}
                placeholder={t('welcomeRecruiter.firstNamePlaceholder')}
                maxLength={100}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                {t('welcomeRecruiter.lastNameLabel')}
                <span className="text-red-500 ml-1">{t('welcomeRecruiter.required')}</span>
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={inputClass}
                placeholder={t('welcomeRecruiter.lastNamePlaceholder')}
                maxLength={100}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                {t('welcomeRecruiter.companyLabel')}
                <span className="text-red-500 ml-1">{t('welcomeRecruiter.required')}</span>
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className={inputClass}
                placeholder={t('welcomeRecruiter.companyPlaceholder')}
                maxLength={200}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                {t('welcomeRecruiter.backgroundLabel')}
              </label>
              <textarea
                value={recruiterBackground}
                onChange={(e) => setRecruiterBackground(e.target.value)}
                rows={4}
                className={`${inputClass} resize-none`}
                placeholder={t('welcomeRecruiter.backgroundPlaceholder')}
                maxLength={2000}
              />
              <p className="mt-2 text-[11px] text-slate-400">
                {t('welcomeRecruiter.backgroundHint')}
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
              <label className="flex items-start cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consentGranted}
                  onChange={(e) => setConsentGranted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-pink-600 border-slate-300 rounded focus:ring-2 focus:ring-pink-500/30 cursor-pointer"
                  required
                />
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-slate-900 leading-relaxed">
                    {t('welcomeRecruiter.consentText')}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    {t('welcomeRecruiter.consentInfo')}
                  </p>
                </div>
              </label>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button
                type="submit"
                disabled={loading || uploadingImage}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 px-6 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {(loading || uploadingImage) && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? t('welcomeRecruiter.submitLoading') : t('welcomeRecruiter.submitButton')}
              </button>

              <button
                type="button"
                onClick={handleSkip}
                disabled={loading}
                className="px-5 py-3 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors disabled:opacity-50"
              >
                {t('welcomeRecruiter.skipButton')}
              </button>
            </div>
          </form>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm">
          <p className="text-sm text-slate-500">
            {t('welcomeRecruiter.footerNote')}
          </p>
        </div>
      </div>
    </RecruiterPageShell>
  );
};

export default WelcomeRecruiter;

